import { Button, Flex, MenuTrigger, Text, useDisclosure } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { FaChevronDown, FaClock, FaKey, FaSignOutAlt, FaUserFriends, FaUsers } from "react-icons/fa";
import { FaUser } from "react-icons/fa6";
import { useNavigate, useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../app/hooks";
import { AclPath, UserPermissionLevel } from "../config/constants";
import { ApiKeyModalBody } from "../features/apiKey/apiKeyModalBody";
import { authSelector, Logout } from "../lib/authentication/authenticationSlice";
import { useAuthContext } from "../lib/authentication/components/useAuthContext";
import { authConnectionSelector } from "../lib/authentication/envSlice";
import { getCompanyFromStaticMap } from "../lib/helper";

import AppSelect from "./appSelect/appSelect";
import { ArtesianTokenErrorModal } from "./artesianTokenErrorModal";
import { ChackraUIBaseModal } from "./chackraModal/chackraBaseModal";
import { useCanRead } from "./protectedByRole/helpers";
import ProtectedComponentByRole from "./protectedByRole/protectedComponentByRole";
import { useColorMode } from "./ui/color-mode";
import { MenuContent, MenuItem, MenuRoot, MenuSeparator } from "./ui/menu";

export const MenuUser: React.FC = () => {
  const canReadApiKey = useCanRead(AclPath.ApiKey);
  const canReadTimeTransforms = useCanRead(AclPath.TimeTransform);
  const canReadConfiguration = canReadApiKey || canReadTimeTransforms;
  const authConnection = useAppSelector(authConnectionSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate(); // Initialize useNavigate
  const location = useLocation();
  const { setColorMode } = useColorMode();
  const { open, onOpen, onClose } = useDisclosure();
  const authStore = useAppSelector(authSelector);
  const user = authStore.data;
  const [selectedTenant, setSelectedTenant] = useState<string>(() => {
    const storedTenant = localStorage.getItem("selectedTenant");
    const availableTenants = user?.userInfo?.tenants ?? [];

    // Check if stored tenant is still valid
    if (storedTenant && availableTenants.includes(storedTenant)) {
      return storedTenant;
    }
    // If stored tenant is invalid or doesn't exist, use first available tenant
    const firstTenant = availableTenants[0] ?? "no tenant";
    // Update localStorage with the valid tenant (or remove if no tenant available)
    if (firstTenant !== "no tenant") {
      localStorage.setItem("selectedTenant", firstTenant);
    } else {
      localStorage.removeItem("selectedTenant");
    }

    return firstTenant;
  });
  const handleTenantChange = (tenant: string | undefined) => {
    if (tenant) {
      localStorage.setItem("selectedTenant", tenant);
      setSelectedTenant(tenant);
      window.location.reload(); // Ricarica la pagina
    }
  };
  const handleUsersClick = async () => {
    await navigate("/users");
  };
  const handleGroupsClick = async () => {
    await navigate("/groups");
  };
  const handleTimeTransformsClick = async () => {
    await navigate("/timeTransforms");
  };
  const tenantOptions =
    user?.userInfo?.tenants?.map(tenant => ({
      label: (
        <>
          <Flex direction="column">
            <Text>{tenant}</Text>
            <Text fontSize="xs" color="brand.muted">
              {getCompanyFromStaticMap(tenant)}
            </Text>
          </Flex>
        </>
      ),
      value: tenant,
    })) ?? [];
  const { context: authProvider } = useAuthContext();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const monitorTokenExpiry = (callback: () => void, bufferMs = 5 * 60 * 1000 + 1000) => {
    const expiryStr = localStorage.getItem("arkTokenExpiry");
    if (!expiryStr) return;

    const expiryTime = parseInt(expiryStr, 10);
    const now = Date.now();
    const timeUntilExpiry = expiryTime - now - bufferMs;

    if (timeUntilExpiry <= 0) {
      callback();
      return;
    }

    setTimeout(() => {
      callback();
    }, timeUntilExpiry);
  };
  useEffect(() => {
    monitorTokenExpiry(() => {
      setShowLoginModal(true);
    });
  }, []);

  useEffect(() => {
    if (authConnection === "K4View") {
      setColorMode("light");
    } else {
      setColorMode("dark");
    }
  }, [authConnection, setColorMode]);
  return (
    <>
      <MenuRoot>
        <MenuTrigger asChild p={2}>
          <Button bg="header" _hover={{ bg: "brand.subtle" }}>
            <FaUser />

            <Text fontSize="sm" fontWeight="bold">
              {user?.userInfo?.username ?? "No Username"}
            </Text>
            <Text fontSize="xs" color="fg" my={2}>
              {selectedTenant}
            </Text>
            <FaChevronDown />
          </Button>
        </MenuTrigger>
        <MenuContent bg="page" color="fg" borderColor="subtle" w="10vw">
          <Text fontSize="xs" color="fg" my={2}>
            Logged in as <br />
            <Text as="span" color="brand.muted">
              {user?.userInfo?.email ?? "No Email"}
            </Text>
          </Text>

          <MenuSeparator />
          {tenantOptions.length > 0 && (
            <>
              <Text fontSize="sm" color="fg" my={2}>
                Select Tenant
              </Text>
              <AppSelect
                options={tenantOptions}
                onChange={handleTenantChange}
                value={selectedTenant}
                placeholder="Select a tenant"
                bg="header"
                size="sm"
                clearable={false}
                zIndex="sticky"
              />
            </>
          )}

          <Text hidden={!canReadConfiguration} fontSize="sm" color="fg" my={2}>
            Configuration
          </Text>
          <MenuSeparator hidden={!canReadConfiguration} />
          <ProtectedComponentByRole
            roleType={UserPermissionLevel.Write}
            pageKey={AclPath.ApiKey}
            children={
              <MenuItem _hover={{ bg: "header" }} value={"apiKeys"} onClick={onOpen}>
                <FaKey />
                API Keys
              </MenuItem>
            }
          />

          <ProtectedComponentByRole
            roleType={UserPermissionLevel.Read}
            pageKey={AclPath.TimeTransform}
            children={
              <MenuItem
                color={location.pathname === "/timeTransforms" ? "brand.highlight" : ""}
                _hover={{ bg: "header" }}
                value={"timeTransforms"}
                onClick={handleTimeTransformsClick}
              >
                <FaClock />
                Time Transforms
              </MenuItem>
            }
          />

          <>
            <MenuSeparator />
            <Text fontSize="sm" color="fg" my={2}>
              Admin Only
            </Text>
            <ProtectedComponentByRole
              roleType={UserPermissionLevel.Read}
              pageKey={AclPath.PrincipalGroup}
              children={
                <MenuItem
                  color={location.pathname === "/users" ? "brand.highlight" : ""}
                  value="users"
                  _hover={{ bg: "header" }}
                  onClick={handleUsersClick}
                >
                  <FaUsers /> Users
                </MenuItem>
              }
            />
            <ProtectedComponentByRole
              roleType={UserPermissionLevel.Read}
              pageKey={AclPath.PrincipalGroup}
              children={
                <MenuItem
                  color={location.pathname === "/groups" ? "brand.highlight" : ""}
                  value="groups"
                  _hover={{ bg: "header" }}
                  onClick={handleGroupsClick}
                >
                  <FaUserFriends />
                  Groups
                </MenuItem>
              }
            />
          </>
          <MenuSeparator />
          <MenuItem value="signout" _hover={{ bg: "header" }} onClick={async () => dispatch(Logout())}>
            Sign Out
            <FaSignOutAlt />
          </MenuItem>
        </MenuContent>
      </MenuRoot>{" "}
      <ArtesianTokenErrorModal />
      <ChackraUIBaseModal open={open} title={"Api Key"} body={<ApiKeyModalBody />} size={"xl"} onClose={onClose} />
      <ChackraUIBaseModal
        open={showLoginModal}
        title="Session expired"
        unclosable
        onSubmit={async () => {
          try {
            if (authProvider?.loginPopup) {
              await authProvider.loginPopup();
            }
            monitorTokenExpiry(() => {
              setShowLoginModal(true);
            });
            setShowLoginModal(false);
          } catch (e) {
            console.error("Login popup failed:", e);
          }
        }}
        size="sm"
        body={
          <Flex direction="column" gap={4} align="center">
            <Text textAlign="center" fontSize="sm" color="fg.muted">
              Your session has expired.
              <br />
              Please refresh session to continue.
            </Text>

            <Button
              onClick={async () => {
                try {
                  if (authProvider?.loginPopup) {
                    await authProvider.loginPopup();
                  }
                  monitorTokenExpiry(() => {
                    setShowLoginModal(true);
                  });
                  setShowLoginModal(false);
                } catch (e) {
                  console.error("Login popup failed:", e);
                }
              }}
              colorScheme="blue"
              size="md"
              px={8}
            >
              Refresh Session
            </Button>
          </Flex>
        }
      />
    </>
  );
};

export default MenuUser;
